// src/components/rodeoAi/ConversationHistory.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    CircularProgress,
    Alert,
    IconButton,
    Button,
    TextField,
    InputAdornment,
    Chip,
    Tooltip,
    Divider,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    useMediaQuery,
    useTheme,
    Skeleton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ChatIcon from '@mui/icons-material/Chat';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
// import ArchiveIcon from '@mui/icons-material/Archive';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MessageIcon from '@mui/icons-material/Message';
import axios from '../../config/axiosConfig';

const ConversationHistory = ({ 
    user, 
    currentConversationId,
    onNewChat,
    embedded = false,
    refreshTrigger = 0
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // State
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [pagination, setPagination] = useState({
        total: 0,
        limit: 20,
        offset: 0,
        hasMore: false
    });

    // Menu and dialog state
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedConv, setSelectedConv] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [loadingAction, setLoadingAction] = useState(false);

    // Get auth headers
    const getAuthHeaders = useCallback(() => {
        if (user?.token) {
            return { Authorization: `Bearer ${user.token}` };
        }
        const token = localStorage.getItem('token');
        if (token) {
            return { Authorization: `Bearer ${token}` };
        }
        return {};
    }, [user?.token]);

    // Fetch conversations
    const fetchConversations = useCallback(async (offset = 0, search = '') => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.get('/api/ai/conversations', {
                params: {
                    limit: 20,
                    offset,
                    search
                },
                headers: getAuthHeaders()
            });

            setConversations(prev => 
                offset === 0 ? response.data.conversations : [...prev, ...response.data.conversations]
            );
            setPagination(response.data.pagination);
        } catch (error) {
            console.error('Failed to fetch conversations:', error);
            setError('Failed to load conversations');
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders]);

    // Initial load
    useEffect(() => {
        fetchConversations();
    }, [fetchConversations]);

    // Refresh when trigger changes (new conversation created)
    useEffect(() => {
        if (refreshTrigger > 0) {
            fetchConversations(0, searchQuery);
        }
    }, [refreshTrigger, fetchConversations, searchQuery]);

    // Handle search with debounce
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            if (searchQuery.length > 0 || searchQuery === '') {
                fetchConversations(0, searchQuery);
            }
        }, 500);

        return () => clearTimeout(delayDebounce);
    }, [searchQuery, fetchConversations]);

    // Handle conversation selection
    const handleSelectConversation = useCallback((conversationId) => {
        if (embedded) {
            navigate(`/chat/${conversationId}`);
        } else {
            navigate(`/chat/${conversationId}`);
        }
    }, [embedded, navigate]);

    // Handle new chat
    const handleNewChat = useCallback(() => {
        if (onNewChat) {
            onNewChat();
        } else {
            navigate('/chat');
        }
    }, [onNewChat, navigate]);

    // Handle menu actions
    const handleMenuOpen = useCallback((event, conv) => {
        event.stopPropagation();
        setMenuAnchor(event.currentTarget);
        setSelectedConv(conv);
    }, []);

    const handleMenuClose = useCallback(() => {
        setMenuAnchor(null);
    }, []);

    // Handle delete
    const handleDelete = useCallback(async () => {
        if (!selectedConv) return;

        try {
            setLoadingAction(true);
            await axios.delete(`/api/ai/conversations/${selectedConv.conversationId}`, {
                headers: getAuthHeaders()
            });

            // Remove from list
            setConversations(prev => 
                prev.filter(c => c.conversationId !== selectedConv.conversationId)
            );
            
            // Close dialog and menu
            setDeleteDialogOpen(false);
            handleMenuClose();

            // If this was the current conversation, redirect to new chat
            if (currentConversationId === selectedConv.conversationId) {
                handleNewChat();
            }
        } catch (error) {
            console.error('Failed to delete conversation:', error);
            setError('Failed to delete conversation');
        } finally {
            setLoadingAction(false);
        }
    }, [selectedConv, getAuthHeaders, handleMenuClose, currentConversationId, handleNewChat]);

    // Handle archive/unarchive
    // const handleArchive = useCallback(async (archive = true) => {
    //     if (!selectedConv) return;

    //     try {
    //         setLoadingAction(true);
    //         await axios.patch(
    //             `/api/ai/conversations/${selectedConv.conversationId}/archive`,
    //             { archive },
    //             { headers: getAuthHeaders() }
    //         );

    //         // Update or remove from list depending on current view
    //         if (archive) {
    //             setConversations(prev => 
    //                 prev.filter(c => c.conversationId !== selectedConv.conversationId)
    //             );
    //         }

    //         handleMenuClose();
    //     } catch (error) {
    //         console.error('Failed to archive conversation:', error);
    //         setError('Failed to update conversation');
    //     } finally {
    //         setLoadingAction(false);
    //     }
    // }, [selectedConv, getAuthHeaders, handleMenuClose]);

    // Format date
    const formatDate = useCallback((date) => {
        const d = new Date(date);
        const now = new Date();
        const diffMs = now - d;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
        
        return d.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
    }, []);

    // Loading skeleton
    const LoadingSkeleton = useCallback(() => (
        <>
            {[1, 2, 3, 4, 5].map(i => (
                <Box key={i} sx={{ px: 2, py: 1.5 }}>
                    <Skeleton variant="text" width="60%" height={24} />
                    <Skeleton variant="text" width="100%" height={20} />
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Skeleton variant="rounded" width={60} height={20} />
                        <Skeleton variant="rounded" width={80} height={20} />
                    </Box>
                </Box>
            ))}
        </>
    ), []);

    return (
        <Paper
            elevation={embedded ? 0 : 2}
            sx={{
                height: embedded ? '100%' : 'calc(100vh - 100px)',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                        ? 'rgba(19, 47, 76, 0.6)'
                        : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: (theme) =>
                    theme.palette.mode === 'dark'
                        ? '1px solid rgba(255, 255, 255, 0.1)'
                        : '1px solid rgba(0, 0, 0, 0.08)',
                borderRadius: embedded ? 0 : 2,
                overflow: 'hidden'
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    p: 2,
                    borderBottom: (theme) =>
                        theme.palette.mode === 'dark'
                            ? '1px solid rgba(255, 255, 255, 0.1)'
                            : '1px solid rgba(0, 0, 0, 0.08)'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ChatIcon sx={{ color: 'primary.main' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Conversations
                        </Typography>
                        {conversations.length > 0 && (
                            <Chip
                                label={pagination.total}
                                size="small"
                                sx={{
                                    height: 22,
                                    backgroundColor: (theme) =>
                                        theme.palette.mode === 'dark'
                                            ? 'rgba(0, 127, 255, 0.2)'
                                            : 'rgba(0, 127, 255, 0.1)',
                                    color: 'primary.main'
                                }}
                            />
                        )}
                    </Box>
                    <Tooltip title="New Chat">
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={handleNewChat}
                            sx={{
                                backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(0, 127, 255, 0.15)'
                                        : 'rgba(0, 127, 255, 0.08)',
                                '&:hover': {
                                    backgroundColor: (theme) =>
                                        theme.palette.mode === 'dark'
                                            ? 'rgba(0, 127, 255, 0.25)'
                                            : 'rgba(0, 127, 255, 0.15)'
                                }
                            }}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Search */}
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search conversations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                            </InputAdornment>
                        )
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: (theme) =>
                                theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : 'rgba(0, 0, 0, 0.02)',
                            '& fieldset': {
                                borderColor: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.1)'
                                        : 'rgba(0, 0, 0, 0.1)'
                            }
                        }
                    }}
                />
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert
                    severity="error"
                    onClose={() => setError(null)}
                    sx={{ mx: 2, mt: 2 }}
                >
                    {error}
                </Alert>
            )}

            {/* Conversations List */}
            <Box
                sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden'
                }}
            >
                {loading && conversations.length === 0 ? (
                    <LoadingSkeleton />
                ) : conversations.length === 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            p: 4,
                            textAlign: 'center'
                        }}
                    >
                        <ChatIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No conversations yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Start a new chat to begin
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleNewChat}
                            size="small"
                        >
                            New Chat
                        </Button>
                    </Box>
                ) : (
                    <List sx={{ p: 0 }}>
                        {conversations.map((conv, index) => (
                            <React.Fragment key={conv.conversationId}>
                                <ListItem
                                    disablePadding
                                    secondaryAction={
                                        <IconButton
                                            edge="end"
                                            size="small"
                                            onClick={(e) => handleMenuOpen(e, conv)}
                                            sx={{ 
                                                opacity: isMobile ? 1 : 0,
                                                transition: 'opacity 0.2s',
                                                '.MuiListItem-root:hover &': {
                                                    opacity: 1
                                                }
                                            }}
                                        >
                                            <MoreVertIcon fontSize="small" />
                                        </IconButton>
                                    }
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: (theme) =>
                                                theme.palette.mode === 'dark'
                                                    ? 'rgba(255, 255, 255, 0.03)'
                                                    : 'rgba(0, 0, 0, 0.02)'
                                        },
                                        ...(currentConversationId === conv.conversationId && {
                                            backgroundColor: (theme) =>
                                                theme.palette.mode === 'dark'
                                                    ? 'rgba(0, 127, 255, 0.1)'
                                                    : 'rgba(0, 127, 255, 0.05)',
                                            borderLeft: '3px solid',
                                            borderLeftColor: 'primary.main'
                                        })
                                    }}
                                >
                                    <ListItemButton
                                        onClick={() => handleSelectConversation(conv.conversationId)}
                                        sx={{ py: 1.5, pr: 6 }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <ChatIcon 
                                                fontSize="small" 
                                                sx={{ 
                                                    color: currentConversationId === conv.conversationId 
                                                        ? 'primary.main' 
                                                        : 'text.secondary' 
                                                }}
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: currentConversationId === conv.conversationId ? 600 : 500,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        pr: 1
                                                    }}
                                                >
                                                    {conv.title}
                                                </Typography>
                                            }
                                            secondary={
                                                <Box>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            display: 'block',
                                                            color: 'text.secondary',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            mb: 0.5
                                                        }}
                                                    >
                                                        {conv.preview}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                            <Typography variant="caption" color="text.secondary">
                                                                {formatDate(conv.lastActiveAt)}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <MessageIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                            <Typography variant="caption" color="text.secondary">
                                                                {conv.messageCount}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            }
                                        />
                                    </ListItemButton>
                                </ListItem>
                                {index < conversations.length - 1 && (
                                    <Divider
                                        sx={{
                                            borderColor: (theme) =>
                                                theme.palette.mode === 'dark'
                                                    ? 'rgba(255, 255, 255, 0.05)'
                                                    : 'rgba(0, 0, 0, 0.05)'
                                        }}
                                    />
                                )}
                            </React.Fragment>
                        ))}

                        {/* Load More */}
                        {pagination.hasMore && (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Button
                                    size="small"
                                    onClick={() => fetchConversations(pagination.offset + pagination.limit, searchQuery)}
                                    disabled={loading}
                                >
                                    Load More
                                </Button>
                            </Box>
                        )}
                    </List>
                )}
            </Box>

            {/* Action Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
            {/*    <MenuItem onClick={() => handleArchive(true)}>
                    <ListItemIcon>
                        <ArchiveIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Archive</ListItemText>
                </MenuItem> */}
                <MenuItem 
                    onClick={() => {
                        setDeleteDialogOpen(true);
                        handleMenuClose();
                    }}
                    sx={{ color: 'error.main' }}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                    </ListItemIcon>
                    <ListItemText>Delete</ListItemText>
                </MenuItem>
            </Menu>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Delete Conversation?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this conversation? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={loadingAction}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                        disabled={loadingAction}
                        startIcon={loadingAction ? <CircularProgress size={16} /> : null}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default React.memo(ConversationHistory);